import { useEffect, useState } from "react";
import { getApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";

const optionsCacheStore = new Map();

export const useQuestionOptions = (question, onOptionsLoaded) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const questionId = question?.questionId || question?.fieldId;
  const questionType = question?.questionType || question?.fieldType;
  const rawOptions = question?.options;

  useEffect(() => {
    const needsFetch =
      ["select", "multiselect", "multi-select"].includes(questionType) &&
      Array.isArray(rawOptions) &&
      rawOptions.length > 0 &&
      typeof rawOptions[0] === "string";

    if (!needsFetch) {
      const opts = rawOptions || [];
      setOptions(opts);
      if (questionId && opts.length > 0) {
        optionsCacheStore.set(questionId, opts);
      }
      if (onOptionsLoaded && questionId && opts.length > 0) {
        onOptionsLoaded(questionId, opts);
      }
      return;
    }

    if (questionId && optionsCacheStore.has(questionId)) {
      const cachedOptions = optionsCacheStore.get(questionId) || [];
      setOptions(cachedOptions);
      setLoading(false);
      onOptionsLoaded?.(questionId, cachedOptions);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await getApi({
          apiUrl: API_URLS.GET_QUESTION_OPTIONS(questionId),
        });

        if (!cancelled && res?.success) {
          const loadedOptions = res.data || [];
          optionsCacheStore.set(questionId, loadedOptions);
          setOptions(loadedOptions);
          onOptionsLoaded?.(questionId, loadedOptions);
        }
      } catch (err) {
        console.error(`Failed options load ${questionId}`, err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [questionId, questionType, rawOptions, onOptionsLoaded]);

  return { options, loading };
};

export const getOptionLabel = (questionId, optionValue, optionsCache = {}) => {
  const options = optionsCache?.[questionId] || [];
  const matched = options.find((option) => option?.value === optionValue);
  return matched?.label || optionValue || "";
};

const QuestionOptions = () => null;

export default QuestionOptions;
